/**
 * useClickOutside Hook
 * 
 * Detects clicks outside a referenced element and triggers a callback.
 * Used for closing dropdowns, modals, and menus when clicking outside.
 * 
 * Uses ref pattern to avoid re-registering listeners when handler changes.
 * This prevents unnecessary event listener churn and potential memory leaks.
 * 
 * @param ref - React ref to the element to monitor
 * @param handler - Callback function (does not need to be memoized)
 * @param enabled - Whether the listener is active (default: true)
 * 
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * useClickOutside(ref, () => setIsOpen(false));
 */

import { useEffect, useRef, RefObject } from "react";

export function useClickOutside<T extends HTMLElement = HTMLElement>(
    ref: RefObject<T | null>,
    handler: (event: MouseEvent | TouchEvent) => void,
    enabled: boolean = true
) {
    // Store handler in ref to avoid re-registering listeners on every render
    const handlerRef = useRef(handler);

    // Update ref when handler changes (no effect re-run)
    useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);

    useEffect(() => {
        if (!enabled) return;

        function handleClickOutside(event: MouseEvent | TouchEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                handlerRef.current(event);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [ref, enabled]); // handler no longer in deps
}
